import axios, { AxiosRequestConfig } from 'axios'
import { ACTIVE_SCHOOL_COOKIE, getBrowserCookie } from '@/lib/utils/cookies'
import { ApiError, isNotAvailableForActiveSchool } from '@/lib/backend-client/api-error'

/**
 * Null-guard a Clerk `getToken()` result before passing it as `accessToken`.
 * `getToken()` resolves to `string | null`; the `backend*` helpers require a
 * non-empty string. Use this instead of a `token as string` cast so a missing
 * session surfaces a clear error rather than sending `Bearer null`.
 */
export const requireToken = (token: string | null | undefined): string => {
  if (!token) throw new Error('No auth token available')
  return token
}

/**
 * The legacy name for `ApiError` (`@/lib/backend-client`), kept as an alias so
 * `instanceof BackendRequestError` matches errors from both the legacy helpers
 * below and the backend client while callers migrate.
 */
export { ApiError as BackendRequestError, isNotAvailableForActiveSchool }

/**
 * The `backend*` helpers RESOLVE an error envelope rather than throwing, so a
 * caller forwarding one straight into TanStack Query gets a "successful" query
 * whose payload is an error. Re-surface it as a thrown `BackendRequestError`
 * that CARRIES THE STATUS, which is what `isNotAvailableForActiveSchool`
 * needs to tell a 403 (routine after a school switch) from a genuine failure.
 */
export function isBackendErrorEnvelope(
  v: unknown
): v is { error: string; status?: number; responseData?: { error?: { message?: string; type?: string } } } {
  return !!v && typeof v === 'object' && typeof (v as { error?: unknown }).error === 'string'
}

export function unwrapBackendResult<T>(res: unknown, fallbackMessage: string): T {
  if (isBackendErrorEnvelope(res)) {
    throw new ApiError(res.responseData?.error?.message ?? fallbackMessage, res.status, {
      type: res.responseData?.error?.type
    })
  }
  return res as T
}

/**
 * Await a `backend*` WRITE and reject if the server refused it, resolving the
 * response otherwise. A save hook returns this so its panel only toasts "saved"
 * after the server confirmed; without it a refused write resolves its error
 * envelope and the panel reports success (SCH-1948). The thrown message is the
 * server's, else `fallbackMessage`, else the envelope's own `error` string.
 */
export async function requireBackendWrite<T>(request: Promise<T>, fallbackMessage?: string): Promise<T> {
  const res = await request
  if (isBackendErrorEnvelope(res)) {
    throw new ApiError(res.responseData?.error?.message ?? fallbackMessage ?? res.error, res.status, {
      type: res.responseData?.error?.type
    })
  }
  return res
}

export const throwIfError = async (response: Promise<{ error?: string; status?: number; responseData?: unknown }>) => {
  // This function is required for useQuery from tanstack to function properly (it needs to throw an error so tanstack is setting isError to true)
  const result = await response
  if (result.error) {
    const type = (result.responseData as { error?: { type?: unknown } } | null | undefined)?.error?.type
    throw new ApiError(result.error, result.status, { type: typeof type === 'string' ? type : undefined })
  }

  return response
}

/**
 * The `x-school-id` override every verb accepts.
 *
 * When set it wins over the active-school cookie — used by the super-admin
 * notification bell so it always targets the admin's own school connection,
 * not whichever school they happen to be viewing.
 */
interface SchoolScopedRequest {
  path: string
  accessToken: string
  withEntireResponse?: boolean
  /** Explicit `x-school-id` override, winning over the active-school cookie. */
  schoolId?: string
  /**
   * How long to wait for this call, in ms. Defaults to {@link DEFAULT_TIMEOUT_MS}.
   *
   * Raise it ONLY for an endpoint whose work is genuinely long and synchronous — the store-asset
   * generator renders and uploads twenty-one images in one request and routinely runs past two
   * minutes. Below its real duration, axios aborts, the caller is told "Network error - no response
   * received from server", and the work carries on at the backend unseen: the operator reads a
   * network fault, and the run's own per-asset failures never reach them.
   */
  timeoutMs?: number
}

/** A verb that carries a request body. */
interface BodyRequest extends SchoolScopedRequest {
  bodyData: object
}

/**
 * The verbs differ ONLY in their HTTP method, so they share one body rather
 * than five near-identical copies. That mattered in practice: `backendPUT`
 * spent months as the one verb without the `schoolId` override because the
 * copies drifted, and a caller needing to act against a non-active school had
 * no way to say so. The per-verb error strings are preserved — they name the
 * function a caller actually called.
 */
async function requestWithBody(method: 'POST' | 'PATCH' | 'PUT', verb: string, args: BodyRequest) {
  const { path, bodyData, accessToken, withEntireResponse = false, schoolId, timeoutMs } = args
  if (!path) throw new Error(`No path provided for ${verb}`)
  if (!bodyData) throw new Error(`No bodyData provided for ${verb}`)
  if (!accessToken) throw new Error(`No accessToken provided for ${verb}`)
  return await axiosRequest(path, method, accessToken, bodyData, withEntireResponse, schoolId, timeoutMs)
}

async function requestWithoutBody(method: 'GET' | 'DELETE', verb: string, args: SchoolScopedRequest) {
  const { path, accessToken, withEntireResponse = false, schoolId, timeoutMs } = args
  if (!path) throw new Error(`No path provided for ${verb}`)
  if (!accessToken) throw new Error(`No accessToken provided for ${verb}`)
  return await axiosRequest(path, method, accessToken, null, withEntireResponse, schoolId, timeoutMs)
}

export const backendPOST = (args: BodyRequest) => requestWithBody('POST', 'backendPOST', args)

export const backendPATCH = (args: BodyRequest) => requestWithBody('PATCH', 'backendPATCH', args)

export const backendPUT = (args: BodyRequest) => requestWithBody('PUT', 'backendPUT', args)

export const backendDELETE = (args: SchoolScopedRequest) => requestWithoutBody('DELETE', 'backendDELETE', args)

export const backendGET = (args: SchoolScopedRequest) => requestWithoutBody('GET', 'backendGET', args)

/**
 * Kept as its own export for the call sites that name it; identical to
 * `backendGET` (both hand back a promise the caller awaits).
 */
export const backendGETAsync = (args: SchoolScopedRequest) => requestWithoutBody('GET', 'backendGETAsync', args)

function configureHeaders(accessToken: string, schoolIdOverride?: string) {
  const headers: any = {
    'Content-Type': 'application/json'
  }

  if (typeof window !== 'undefined') {
    // Add demo mode header if enabled in localStorage
    const isDemoMode = localStorage.getItem('demo-mode') === 'true'
    if (isDemoMode) {
      headers['x-demo-mode'] = 'true'
    }
  }

  // Add school context header. An explicit `schoolIdOverride` (e.g. the
  // super-admin notification bell, which must always read the admin's OWN
  // school feed regardless of which school they're currently viewing) wins over
  // the active-school cookie (`ACTIVE_SCHOOL_COOKIE`) written by SchoolProvider.
  const schoolId =
    schoolIdOverride ?? (typeof window !== 'undefined' ? getBrowserCookie(ACTIVE_SCHOOL_COOKIE) : undefined)
  if (schoolId) {
    headers['x-school-id'] = schoolId
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }

  return headers
}

/** The wait every call gets unless it asks for more. */
const DEFAULT_TIMEOUT_MS = 60000

async function axiosRequest(
  path: string,
  method: 'GET' | 'POST' | 'DELETE' | 'PATCH' | 'PUT' = 'GET',
  accessToken: string,
  bodyData: any = null,
  withEntireResponse = false,
  schoolId?: string,
  timeoutOverrideMs?: number
): Promise<any> {
  const timeoutMs = timeoutOverrideMs ?? DEFAULT_TIMEOUT_MS

  try {
    const headers = configureHeaders(accessToken, schoolId)

    const fullUrl =
      path.includes('http') || path.includes('https') ? path : `${process.env.NEXT_PUBLIC_BACKEND_URL}${path}`

    const requestConfig: AxiosRequestConfig = {
      method: method,
      url: fullUrl,
      headers: headers,
      timeout: timeoutMs
    }

    // Don't get rid of this, you can't sent null data on a GET!
    if (bodyData) {
      requestConfig.data = bodyData
    }

    const response = await axios(requestConfig)

    if (withEntireResponse) {
      return response
    } else {
      return response.data
    }
  } catch (error: any) {
    const fullUrl =
      path.includes('http') || path.includes('https') ? path : `${process.env.NEXT_PUBLIC_BACKEND_URL}${path}`

    // Simplified error logging - treat 428 as expected/informational
    if (error.response?.status === 428) {
      console.info(
        `ℹ️ Backend ${method} ${fullUrl} - School setup required:`,
        error.response.data?.message || 'Missing critical data'
      )
    } else {
      console.error(`❌ Backend ${method} ${fullUrl} failed:`, error.message)
      if (error.response?.status) {
        // The API's error body is `{ error: { type, message } }`; a bare `message` is the older
        // shape. Reading only the latter printed "No details" for every typed refusal.
        const backendMessage = error.response.data?.error?.message || error.response.data?.message
        console.error(`   Status: ${error.response.status} - ${backendMessage || 'No details'}`)
      }
    }

    if (error.response) {
      const { status } = error.response
      switch (status) {
        case 401:
          // Handle expired session
          return {
            error: 'Session expired. Please log in again.',
            status,
            bodyData,
            responseData: error.response.data,
            isFatal: true
          }
        case 400:
          // Handle bad request
          return {
            error: 'Bad request.',
            status,
            bodyData,
            responseData: error.response.data,
            isFatal: false
          }
        case 500:
          // Handle server error
          return {
            error: 'Server error.',
            status,
            bodyData,
            responseData: error.response.data,
            isFatal: false
          }
        default:
          return {
            error: 'Unknown error occurred.',
            status,
            bodyData,
            responseData: error.response.data,
            isFatal: false
          }
      }
    } else if (error.request) {
      return {
        error: 'Network error - no response received from server.',
        status: 0,
        bodyData,
        responseData: null,
        isFatal: false
      }
    } else {
      return {
        error: `Request setup error: ${error.message}`,
        status: 0,
        bodyData,
        responseData: null,
        isFatal: false
      }
    }
  }
}
